import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { ageRatingBoard } from 'Helpers/Geo/data';
import env from 'Env';
import ProductRequirements from 'Helpers/Product/ProductRequirements';
import DetailItem from 'Features/Product/components/ShowcaseDetails/DetailItem';
import SupportedLanguages from 'Features/Product/components/ProductAboutSection/SupportedLanguages';
import SteamDeckInformation from 'Components/Utility/SteamDeck/SteamDeckInformation';
import RegionLocked from 'Components/Regional/RegionLocked';

export const titleMessages = defineMessages({
  publishers: {
    id: 'showcase-details.publishers',
    defaultMessage: '{count, plural, one {Publisher} other {Publishers}}',
  },
  systems: {
    id: 'showcase-details.systems',
    defaultMessage: '{count, plural, one {Operating System} other {Operating Systems}}',
  },
  platforms: {
    id: 'showcase-details.platforms',
    defaultMessage: '{count, plural, one {Platform} other {Platforms}}',
  },
  combos: {
    id: 'showcase-details.combos',
    defaultMessage: '{count, plural, one {Collection} other {Collections}}',
  },
  themes: {
    id: 'showcase-details.themes',
    defaultMessage: '{count, plural, one {Theme} other {Themes}}',
  },
  playstyles: {
    id: 'showcase-details.playstyles',
    defaultMessage: '{count, plural, one {Play Style} other {Play Styles}}',
  },
  developers: {
    id: 'showcase-details.developers',
    defaultMessage: '{count, plural, one {Developer} other {Developers}}',
  },
  released: {
    id: 'showcase-details.released',
    defaultMessage: 'Release Date',
  },
  orderDate: {
    id: 'showcase-details.orderDate',
    defaultMessage: 'Order Date',
  },
  earlyAccessDate: {
    id: 'showcase-details.earlyAccessDate',
    defaultMessage: 'Early Access Date',
  },
  earlyAccessToolTip: {
    id: 'showcase-details.earlyAccessToolTip',
    defaultMessage: 'You can start playing your game from this date.',
  },
  genres: {
    id: 'showcase-details.genres',
    defaultMessage: '{count, plural, one {Genre} other {Genres}}',
  },
  features: {
    id: 'showcase-details.features',
    defaultMessage: '{count, plural, one {Feature} other {Features}}',
  },
  franchises: {
    id: 'showcase-details.franchise',
    defaultMessage: '{count, plural, one {Franchise} other {Franchises}}',
  },
  link: {
    id: 'showcase-details.Link',
    defaultMessage: 'Link',
  },
  rating: {
    id: 'showcase-details.rating',
    defaultMessage: 'Rating',
  },
  supported_languages: {
    id: 'showcase-details.supportedlanguages',
    defaultMessage: 'Languages',
  },
  playable_on: {
    id: 'showcase-details.playableon',
    defaultMessage: 'Playable On',
  },
  latest_version: {
    id: 'showcase-details.latestversion',
    defaultMessage: 'Latest version always supplied',
  },
  activates_in: {
    id: 'showcase-details.activatesin',
    defaultMessage: 'Region',
  },
  redeems_in: {
    id: 'showcase-details.redeemsin',
    defaultMessage: 'Redeems In',
  },
  steam_deck_support: {
    id: 'showcase-details.steamdecksupport',
    defaultMessage: 'Steam Deck Support',
  },
  steam_reviews: {
    id: 'showcase-details.steamreviews',
    defaultMessage: 'Steam Reviews',
  },
});

const ShowcaseDetails = ({ product }) => {
  const { formatMessage } = useIntl();

  const requirements = ProductRequirements.ShowRequirements(product);
  const dates = ProductRequirements.ReleaseDates(product);
  const currentDrm = ProductRequirements.CurrentDrm(product);

  const releaseDate = get(dates, 'releaseDate', '');
  const earlyAccess = get(dates, 'earlyAccess', false);
  const earlyAccessDate = get(dates, 'playableFrom', '');

  const platforms = get(requirements, 'validPlatforms', []);
  const xboxPlatforms = get(product, 'xbox_platforms', {});
  const operatingSystems = get(product, 'platforms', {});
  const publishers = get(product, 'publishers', []);
  const developers = get(product, 'developers', []);
  const franchises = get(product, 'franchises', []);
  const ztormId = get(product, 'ztormId', null);
  const ztormOrderDate = get(product, 'order_date', '');
  const ageRatings = get(product, 'age', {});
  const languages = get(product, 'lang', []);
  const steamData = get(product, 'steam', {});
  const vrSupport = get(product, 'vr_support', {});
  const steamReviewSummary = get(product, 'steamReviewSummary', {});
  const soldOut = get(product, 'sold_out', false);
  const productType = get(product, 'type', '');
  const excludedRegions = get(product, 'regions_excluded', []);
  const includedRegions = get(product, 'regions_included', []);

  if (platforms.includes('oculus')) {
    if (!isEmpty(vrSupport) && vrSupport.oculus_rift) platforms.push('Oculus Rift');
    if (!isEmpty(vrSupport) && vrSupport.oculus_meta_quest) platforms.push('Meta Quest');
    if (!isEmpty(vrSupport) && vrSupport.oculus_meta_quest_two) platforms.push('Meta Quest 2');
    if (!isEmpty(vrSupport) && vrSupport.oculus_meta_quest_three) platforms.push('Meta Quest 3');
  }

  const validOperatingStytems = [];
  if (operatingSystems && operatingSystems.windows) validOperatingStytems.push('windows');
  if (operatingSystems && operatingSystems.mac) validOperatingStytems.push('mac');
  if (operatingSystems && operatingSystems.linux) validOperatingStytems.push('linux');

  const xboxPlaysOn = Object.keys(xboxPlatforms).filter(d => xboxPlatforms[d]);

  const releaseDay = new Date(releaseDate).getDate();
  const releaseMonth = new Date(releaseDate).getMonth();

  const useShortReleaseDate = releaseDay === 31 && releaseMonth === 11 && Date.parse(releaseDate) > Date.now(); // eslint-disable-line max-len

  let isBitdefenderProduct = publishers.includes('bitdefender');
  if (publishers && publishers.length === 1 && publishers[0].name && publishers[0].name.toLowerCase() === 'bitdefender') {
    isBitdefenderProduct = true;
  }
  if (publishers && publishers.length === 1 && publishers[0].toLowerCase && publishers[0].toLowerCase() === 'bitdefender') {
    isBitdefenderProduct = true;
  }

  const displayThresholds = {
    ESRB: 17,
    USK: 16,
    PEGI: 18,
    ACB: 18,
  };

  const showRatings = ageRatings[ageRatingBoard] >= displayThresholds[ageRatingBoard];

  const steamThreshold = env.steamPercentThreshold;
  let steamReviewScore = 0;
  const steamReviewScoreDesc = steamReviewSummary?.review_score_desc;
  if (steamReviewSummary) {
    steamReviewScore = Math.round((steamReviewSummary.total_positive / steamReviewSummary.total_reviews) * 100); // eslint-disable-line max-len
  }
  const showSteamReviewScore = steamReviewScore >= steamThreshold && currentDrm === 'steam';

  const showActivatesIn = !soldOut && currentDrm !== 'voucher' && currentDrm !== 'redeem';

  return (
    <div className="ShowcaseDetails">
      <div className="ShowcaseHeading">
        <FormattedMessage id="showcase.product.title.details" defaultMessage="Details" />
      </div>

      <div className="ShowcaseDetails__details">
        {showActivatesIn &&
          <div className="DetailItem">
            <div className="DetailItem__main">
              {productType === 'gift-card' ? formatMessage(titleMessages.redeems_in) : formatMessage(titleMessages.activates_in)}
            </div>
            <div className="DetailItem__support">
              <RegionLocked
                shortText
                included={includedRegions}
                excluded={excludedRegions}
                ztormId={ztormId}
              />
            </div>
          </div>
        }

        {!isEmpty(platforms) &&
          <DetailItem
            type="platform"
            title={formatMessage(titleMessages.platforms, { count: platforms.length })}
            items={platforms}
          />
        }
        {!isEmpty(validOperatingStytems) &&
          <DetailItem
            type="systems"
            title={formatMessage(titleMessages.systems, { count: validOperatingStytems.length })}
            items={validOperatingStytems}
          />
        }
        {!isEmpty(xboxPlaysOn) &&
          <DetailItem
            type="xboxPlatforms"
            title={formatMessage(titleMessages.playable_on)}
            items={xboxPlaysOn}
          />
        }
        {releaseDate && !isBitdefenderProduct &&
          <DetailItem
            type="date"
            shortDate={useShortReleaseDate}
            title={formatMessage(titleMessages.released)}
            item={releaseDate}
          />
        }
        {!earlyAccess && Date.now() > Date.parse(earlyAccessDate) &&
          <DetailItem
            type="date"
            title={formatMessage(titleMessages.earlyAccessDate)}
            item={earlyAccessDate}
            tooltipText={formatMessage(titleMessages.earlyAccessToolTip)}
          />
        }
        {ztormOrderDate && new Date().toISOString() < ztormOrderDate &&
          <DetailItem
            type="date"
            title={formatMessage(titleMessages.orderDate)}
            item={ztormOrderDate}
          />
        }
        {!isEmpty(developers) &&
          <DetailItem
            type="developers"
            title={formatMessage(titleMessages.developers, { count: developers.length })}
            items={developers}
          />
        }
        {!isEmpty(publishers) &&
          <DetailItem
            type="publishers"
            title={formatMessage(titleMessages.publishers, { count: publishers.length })}
            items={publishers}
          />
        }
        {!isEmpty(franchises) &&
          <DetailItem
            type="franchises"
            title={formatMessage(titleMessages.franchises, { count: franchises.length })}
            items={franchises}
          />
        }
        {showRatings &&
          <DetailItem
            type="age"
            title={formatMessage(titleMessages.rating)}
            item={`${ageRatingBoard} ${ageRatings[ageRatingBoard]}`}
          />
        }
        {!isEmpty(languages) &&
          <div className="DetailItem DetailItem--centred">
            <div className="DetailItem__main">
              {formatMessage(titleMessages.supported_languages)}
            </div>
            <div className="DetailItem__support">
              <SupportedLanguages languages={languages} />
            </div>
          </div>
        }
        {showSteamReviewScore &&
          <DetailItem
            type="steamReviews"
            title={formatMessage(titleMessages.steam_reviews)}
            item={`${steamReviewScore}% ${steamReviewScoreDesc}`}
          />
        }
        {!isEmpty(steamData) && steamData.deck_support &&
          <div className="DetailItem DetailItem--centred">
            <div className="DetailItem__main">
              {formatMessage(titleMessages.steam_deck_support)}
            </div>
            <div className="DetailItem__support">
              {(steamData.deck_support === 'verified' || steamData.deck_support === 'playable') &&
                <SteamDeckInformation
                  type={steamData.deck_support}
                  details={steamData.deck_details}
                  note={steamData.deck_note}
                  product={name}
                />
              }
              {steamData.deck_support === 'unsupported' &&
                <span><FormattedMessage id="showcase-details.steam_deck_support.unsupported" defaultMessage="Unsupported" /></span>
              }
              {steamData.deck_support === 'unknown' &&
                <span><FormattedMessage id="showcase-details.steam_deck_support.unknown" defaultMessage="Unknown" /></span>
              }
            </div>
          </div>
        }
      </div>
    </div>
  );
};

ShowcaseDetails.propTypes = {
  product: PropTypes.shape({}),
};

ShowcaseDetails.defaultProps = {
  product: {},
};

export default ShowcaseDetails;
